import { useAppStore } from "@src/AppStore";
import { apiDRSCostItemCreate } from "@src/apis/drs-cost-item-create";
import { apiDRSCostItemDelete } from "@src/apis/drs-cost-item-delete";
import { apiDRSLabourCost } from "@src/apis/drs-labour-cost";
import { apiDRSProfitabilityUpdate } from "@src/apis/drs-profitability-update";
import { DRSSectionName } from "@src/modules/DRS/DRSPage";
import { DRSCostCategory, DRSProjectCostItem } from "@src/types/drs/DRS";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import ActivityHistory from "./ActivityHistory";
import { useDRS } from "../../useDRS";

const COST_CATEGORY_LABELS: Record<DRSCostCategory, string> = {
  [DRSCostCategory.ShiftCost]: "Custom Items",
  [DRSCostCategory.MobileAsset]: "Assets",
  [DRSCostCategory.Subcontractor]: "Subcontractor",
  [DRSCostCategory.Material]: "Materials",
  [DRSCostCategory.Cartage]: "Cartage Cost",
  [DRSCostCategory.Labour]: "Labour",
};

const ShiftProfitability: React.FC<{ drsPageType: string }> = () => {
  const { drs, refresh, updateSectionViaPath } = useDRS();
  const { defaultRoleId } = useAppStore();

  // State variables
  const [projectRevenue, setProjectRevenue] = useState<string>("");
  const [planProjectRevenue, setPlanProjectRevenue] = useState<string>("");
  const [comments, setComments] = useState<string>("");
  const [isSavingRevenue, setIsSavingRevenue] = useState(false);
  const [isSavingPlanRevenue, setIsSavingPlanRevenue] = useState(false);
  const [isSavingComments, setIsSavingComments] = useState(false);
  const [costItems, setCostItems] = useState<DRSProjectCostItem[]>([]);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [modalDescription, setModalDescription] = useState("");
  const [modalPrice, setModalPrice] = useState("");
  const [modalPlanPrice, setModalPlanPrice] = useState("");
  const [modalCategory, setModalCategory] = useState<DRSCostCategory>(DRSCostCategory.ShiftCost);
  const [modalMode, setModalMode] = useState<"plan" | "actual">("actual");
  const [isSavingModal, setIsSavingModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<DRSProjectCostItem | null>(null);
  const [labourCost, setLabourCost] = useState<number>(0);
  const [isLoadingLabourCost, setIsLoadingLabourCost] = useState(false);

  // Which cost category sections are expanded in the comparison view.
  // Collapsed by default so users see aligned totals first; Custom Items
  // starts open so the add-cost-item buttons are visible.
  const [expandedCats, setExpandedCats] = useState<Record<string, boolean>>({ custom: true });
  const toggleCat = (key: string) => setExpandedCats((prev) => ({ ...prev, [key]: !prev[key] }));

  // Spray Seal Material Rate Modal State
  const [showSpraySealRateModal, setShowSpraySealRateModal] = useState(false);
  const [selectedSpraySealMaterial, setSelectedSpraySealMaterial] = useState<any>(null);
  const [spraySealModalCost, setSpraySealModalCost] = useState("");
  const [isSavingSpraySealRate, setIsSavingSpraySealRate] = useState(false);

  // Fetch labour cost from API
  const fetchLabourCost = React.useCallback(async () => {
    if (!drs?.jobDetails?.id) return;

    // Get depotPayroll from the project depot
    const depotId = drs.jobDetails.project.depot.id;
    if (!depotId) {
      console.warn("No depot ID available from project for labour cost calculation");
      return;
    }

    setIsLoadingLabourCost(true);
    try {
      const response = await apiDRSLabourCost({
        drsSummaryId: drs.jobDetails.id.toString(),
        depotPayroll: depotId,
      });

      // Response structure: { data: { total_cost: "7848.00", breakdown: {...} }, message: "" }
      const totalCost = parseFloat(response.data.total_cost);
      setLabourCost(isNaN(totalCost) ? 0 : totalCost);

    } catch (error) {
      console.error("Error fetching labour cost:", error);
      setLabourCost(0);
    } finally {
      setIsLoadingLabourCost(false);
    }
  }, [drs]);

  // Initialize state from DRS data
  useEffect(() => {
    if (drs) {
      // Initialize comments from DRS data
      if (drs.jobDetails.comments) {
        setComments(drs.jobDetails.comments);
      }

      // Initialize project revenue from DRS data
      if (drs.jobDetails.projectRevenue !== null && drs.jobDetails.projectRevenue !== undefined) {
        setProjectRevenue(drs.jobDetails.projectRevenue.toString());
      }

      // Initialize plan project revenue from DRS data
      if (drs.jobDetails.planProjectRevenue !== null && drs.jobDetails.planProjectRevenue !== undefined) {
        setPlanProjectRevenue(drs.jobDetails.planProjectRevenue.toString());
      }

      // Initialize cost items from DRS data
      if (drs.projectCostItems) {
        setCostItems(drs.projectCostItems);
      }

      // Fetch labour cost when DRS loads
      fetchLabourCost();
    }
  }, [drs, fetchLabourCost]);

  if (!drs) {
    return <div>Loading...</div>;
  }

  // Utility function to safely format numbers
  const safeToFixed = (value: any, decimals: number = 2): string => {
    const num = Number(value);
    if (isNaN(num) || !isFinite(num)) {
      return (0).toFixed(decimals);
    }
    return num.toFixed(decimals);
  };

  // Get mobile assets from the API response - only include assets with actual or edit data
  const getMobileAssets = () => {
    if (!drs) return [];

    const assets = [];

    // Get asphalt assets - filter to only include assets with meaningful actual or edit data
    if (drs.mobileAssets?.boral?.asphalt?.assets) {
      const filteredAsphaltAssets = drs.mobileAssets.boral.asphalt.assets.filter((asset) => {
        // Check if actual has meaningful data (available and has mobileAsset)
        const hasActualData = asset.actual?.available && asset.actual?.mobileAsset;

        // Check if edit has meaningful data (available and has mobileAsset)
        const hasEditData = asset.edit?.available && asset.edit?.mobileAsset;

        // Only include assets that have actual or edit data (ignore plan-only assets)
        return hasActualData || hasEditData;
      });
      assets.push(...filteredAsphaltAssets);
    }

    // Get spray seal assets - filter to only include assets with meaningful actual or edit data
    if (drs.mobileAssets?.boral?.spraySeal?.assets) {
      const filteredSprayAssets = drs.mobileAssets.boral.spraySeal.assets.filter((asset) => {
        // Check if actual has meaningful data (available and has mobileAsset)
        const hasActualData = asset.actual?.available && asset.actual?.mobileAsset;

        // Check if edit has meaningful data (available and has mobileAsset)
        const hasEditData = asset.edit?.available && asset.edit?.mobileAsset;

        // Only include assets that have actual or edit data (ignore plan-only assets)
        return hasActualData || hasEditData;
      });
      assets.push(...filteredSprayAssets);
    }

    return assets;
  };

  const getAssetDescription = (asset: any) => {
    // Priority: edit -> actual (plan data ignored)
    return asset.edit?.mobileAsset?.id || asset.actual?.mobileAsset?.id || "Unknown Asset";
  };

  const getAssetRate = (asset: any) => {
    // Use internalHireRate from the root level first, then from edit/actual (plan data ignored)
    const rootRate = asset.internalHireRate;
    const editRate = asset.edit?.mobileAsset?.internalHireRate;
    const actualRate = asset.actual?.mobileAsset?.internalHireRate;

    if (rootRate !== null && rootRate !== undefined) {
      return `$${rootRate}`;
    } else if (editRate !== null && editRate !== undefined) {
      return `$${editRate}`;
    } else if (actualRate !== null && actualRate !== undefined) {
      return `$${actualRate}`;
    }
    return ""; // Don't show any value if null
  };

  const getAssetQuantity = (asset: any) => {
    // Priority: edit -> actual (plan data ignored)
    const hours = asset.edit?.hours ?? asset.actual?.hours;
    return hours !== null && hours !== undefined ? hours : "";
  };

  const getAssetTotal = (asset: any) => {
    const rate = asset.internalHireRate ?? asset.edit?.mobileAsset?.internalHireRate ?? asset.actual?.mobileAsset?.internalHireRate;
    const hours = asset.edit?.hours ?? asset.actual?.hours;

    if (rate !== null && rate !== undefined && hours !== null && hours !== undefined) {
      return rate * hours;
    }
    return 0;
  };

  const sumSection = <T,>(items: T[] | undefined, getValue: (item: T) => number): number => {
    return (items || []).reduce((total, item) => {
      const v = Number(getValue(item));
      return total + (isNaN(v) || !isFinite(v) ? 0 : v);
    }, 0);
  };

  const getCustomItemsForCategory = (category: DRSCostCategory) =>
    costItems.filter((item) => (item.category || DRSCostCategory.ShiftCost) === category);

  // Items created from the Plan side carry a planItemPrice; items created from
  // the Actual side leave it null. Keep each side's detail rows separate.
  const getPlanCustomItemsForCategory = (category: DRSCostCategory) =>
    getCustomItemsForCategory(category).filter((item) => item.planItemPrice != null);

  const getActualCustomItemsForCategory = (category: DRSCostCategory) =>
    getCustomItemsForCategory(category).filter((item) => item.planItemPrice == null);

  const sumCustomItems = (category: DRSCostCategory) =>
    sumSection(getCustomItemsForCategory(category), (item) => Number(item.itemPrice || 0));

  // Plan-side mirror: sums planItemPrice for custom items in the category.
  // Treats NULL plan prices as 0 so legacy rows don't break the total.
  const sumPlanCustomItems = (category: DRSCostCategory) =>
    sumSection(getCustomItemsForCategory(category), (item) =>
      Number(item.planItemPrice ?? 0));

  const getMobileAssetsSubtotal = () =>
    sumSection(getMobileAssets(), getAssetTotal) + sumCustomItems(DRSCostCategory.MobileAsset);
  const getSubcontractorSubtotal = () =>
    sumSection(subcontractorItems, getSubcontractorTotal) + sumCustomItems(DRSCostCategory.Subcontractor);
  const getMaterialsSubtotal = () =>
    sumSection(getMaterialsData(), getMaterialTotal) + sumCustomItems(DRSCostCategory.Material);
  const getCartageCostSubtotal = () =>
    sumSection(getCartageCostData(), getCartageCostTotal) + sumCustomItems(DRSCostCategory.Cartage);
  const getCustomItemsSubtotal = () => sumCustomItems(DRSCostCategory.ShiftCost);
  const getLabourSubtotal = () => (Number(labourCost) || 0) + sumCustomItems(DRSCostCategory.Labour);

  const getTotalCosts = () => {
    try {
      const total =
        getMobileAssetsSubtotal() +
        getSubcontractorSubtotal() +
        getMaterialsSubtotal() +
        getCartageCostSubtotal() +
        getCustomItemsSubtotal() +
        getLabourSubtotal();
      return isNaN(total) || !isFinite(total) ? 0 : total;
    } catch (error) {
      console.error("Error in getTotalCosts:", error);
      return 0;
    }
  };

  const getSubtotal = () => {
    const total = getTotalCosts();
    // Ensure we return a number, not a string
    return Number(total);
  };

  const getOverheadAmount = () => {
    const subtotal = getSubtotal();
    const overheadPercentage = drs?.jobDetails?.project?.percentageOverhead || 0;
    return (subtotal * overheadPercentage) / 100;
  };

  const getTotalCostsWithOverhead = () => {
    const subtotal = getSubtotal();
    const overhead = getOverheadAmount();

    // Ensure both are numbers before addition
    const subtotalNum = Number(subtotal);
    const overheadNum = Number(overhead);
    const result = subtotalNum + overheadNum;

    // Safety check - if we get a bad result, return 0
    if (isNaN(result) || !isFinite(result)) {
      return 0;
    }

    return result;
  };

  const getShiftMargin = () => {
    const revenue = projectRevenue ? parseFloat(projectRevenue) : 0;
    const totalCosts = getTotalCostsWithOverhead();
    const margin = revenue - totalCosts;
    return isNaN(margin) ? 0 : margin;
  };

  // ============================================================
  // PLAN-side aggregate cost helpers
  // Mirrors the actual helpers but reads from .plan (or stored
  // *CostSummary.planTotalCost where the per-row plan data is
  // not yet enriched in the API payload). Returns category totals
  // for the side-by-side Plan panel.
  // ============================================================

  const getPlanMobileAssetsSubtotal = (): number => {
    if (!drs) return 0;
    let total = 0;
    const sumAssets = (assets: any[]) => {
      (assets || []).forEach((asset) => {
        const rate = asset.plan?.mobileAsset?.internalHireRate ?? asset.internalHireRate;
        const hours = asset.plan?.hours;
        if (rate !== null && rate !== undefined && hours !== null && hours !== undefined) {
          total += Number(rate) * Number(hours);
        }
      });
    };
    sumAssets(drs.mobileAssets?.boral?.asphalt?.assets);
    sumAssets(drs.mobileAssets?.boral?.spraySeal?.assets);
    return total + sumPlanCustomItems(DRSCostCategory.MobileAsset);
  };

  const getPlanSubcontractorSubtotal = (): number => {
    if (!drs?.subcontractor) return 0;
    let total = 0;

    const sumProfiling = (items: any[]) => {
      (items || []).forEach((it) => {
        const rate = it.plan?.supplierAgreement?.unitRate;
        const hours = it.plan?.hours;
        if (rate != null && hours != null) total += Number(rate) * Number(hours);
      });
    };
    const sumGeneral = (items: any[]) => {
      (items || []).forEach((it) => {
        const rate = it.plan?.supplierAgreement?.unitRate;
        const qty = it.plan?.quantity;
        if (rate != null && qty != null) total += Number(rate) * Number(qty);
      });
    };
    const sumTransport = (items: any[]) => {
      (items || []).forEach((it) => {
        const rate = it.plan?.supplier?.unitRate ?? it.plan?.supplierAgreement?.unitRate;
        const qty = it.plan?.quantity;
        if (rate != null && qty != null) total += Number(rate) * Number(qty);
      });
    };

    const asphalt = drs.subcontractor.asphalt;
    if (asphalt) {
      sumProfiling(asphalt.profilingAssets);
      sumGeneral(asphalt.generalAssets);
      sumTransport(asphalt.deliveryTransport);
      sumTransport(asphalt.profilingTransport);
    }
    const sprayseal = drs.subcontractor.spraySeal;
    if (sprayseal) {
      sumGeneral(sprayseal.generalAssets);
      sumTransport(sprayseal.deliveryTransport);
    }
    return total + sumPlanCustomItems(DRSCostCategory.Subcontractor);
  };

  // Material rates are frozen in DRSMaterialCostSummary.costBreakdown when the
  // cost is calculated. Reference data ("Product"."Price", "SprayBinderType"."Price")
  // is repriced in place, so reading it live would retroactively change the cost of
  // a shift that was already costed at the old rate. Always prefer the stored rate.
  //
  // Returns null when the row has no stored entry, which happens before the first
  // calculation and for rows the cost engine skipped (zero or invalid quantity).
  // Callers fall back to the live reference price in that case.
  const getStoredMaterialRate = (
    materialId: any,
    category: string,
    mode: "actual" | "plan"
  ): number | null => {
    if (materialId == null) return null;

    const materialType =
      category === "spraySealBinder"
        ? "SPRAY_SEAL_BINDER"
        : category === "spraySeal"
        ? "SPRAY_SEAL_AGGREGATE"
        : "ASPHALT_PRODUCT";

    const summaries = (drs?.material as any)?.materialCostSummary;
    if (!Array.isArray(summaries)) return null;

    for (const row of summaries) {
      const breakdown = mode === "plan" ? row?.planCostBreakdown : row?.costBreakdown;
      const stored = (breakdown?.materials || []).find(
        (m: any) =>
          String(m?.materialId) === String(materialId) &&
          m?.materialType === materialType
      );
      if (stored?.rate != null) {
        const rate = Number(stored.rate);
        if (isFinite(rate)) return rate;
      }
    }
    return null;
  };

  // Sum plan material cost across all asphalt + spray-seal rows.
  // Prefer the stored DRSMaterialCostSummary.planTotalCost (minus cartage +
  // fuel which surface as their own row), falling back to per-row plan calc.
  const getPlanMaterialsSubtotal = (): number => {
    if (!drs?.material) return 0;
    let total = 0;

    // Asphalt: plan rate * plan.tonnes per detail row
    (drs.material.asphalt?.detail || []).forEach((m: any) => {
      const storedRate = getStoredMaterialRate(m?.id, "asphalt", "plan");
      const price = storedRate !== null ? storedRate : m.plan?.product?.price;
      const tonnes = m.plan?.tonnes;
      if (price != null && tonnes != null) total += Number(price) * Number(tonnes);
    });

    // Spray seal: binder (rate * residualBinder) + aggregate (materialRate * aggregateUsed)
    (drs.material.spraySeal?.detail || []).forEach((m: any) => {
      const storedBinderRate = getStoredMaterialRate(m?.id, "spraySealBinder", "plan");
      const binderPrice =
        storedBinderRate !== null ? storedBinderRate : m.plan?.binderType?.price;
      const residualBinder = m.plan?.residualBinder;
      if (binderPrice != null && residualBinder != null) {
        total += Number(binderPrice) * Number(residualBinder);
      }
      const aggregateUsed = m.plan?.aggregateUsed;
      const matRate = (m as any).materialRate;
      if (matRate != null && aggregateUsed != null) {
        total += Number(matRate) * Number(aggregateUsed);
      }
    });

    return total + sumPlanCustomItems(DRSCostCategory.Material);
  };

  // Plan cartage is stored in DRSMaterialCostSummary.planCartageCost +
  // planFuelCost (only on asphalt rows). Read from the embedded summary.
  const getPlanCartageSubtotal = (): number => {
    const summary = (drs?.material as any)?.materialCostSummary;
    let total = 0;
    if (Array.isArray(summary)) {
      total = summary.reduce((acc: number, row: any) => {
        const c = Number(row?.planCartageCost || 0);
        const f = Number(row?.planFuelCost || 0);
        return acc + (isNaN(c) ? 0 : c) + (isNaN(f) ? 0 : f);
      }, 0);
    }
    return total + sumPlanCustomItems(DRSCostCategory.Cartage);
  };

  // Plan labour: read from DRSLabourCostSummary.planTotalCost when the
  // labour cost summary is embedded in the DRS payload, else 0.
  const getPlanLabourSubtotal = (): number => {
    const planTotal = (drs?.labour as any)?.costSummary?.planTotalCost;
    const labourBase = planTotal != null ? Number(planTotal) : 0;
    return labourBase + sumPlanCustomItems(DRSCostCategory.Labour);
  };

  // Plan-side equivalent of "Custom Items" line in the Actual panel.
  // Sums planItemPrice for items whose category is ShiftCost.
  const getPlanCustomItemsSubtotal = (): number =>
    sumPlanCustomItems(DRSCostCategory.ShiftCost);

  const getPlanSubtotal = (): number => {
    return (
      getPlanMobileAssetsSubtotal() +
      getPlanSubcontractorSubtotal() +
      getPlanMaterialsSubtotal() +
      getPlanCartageSubtotal() +
      getPlanLabourSubtotal() +
      getPlanCustomItemsSubtotal()
    );
  };

  const getPlanOverheadAmount = (): number => {
    const subtotal = getPlanSubtotal();
    const overheadPercentage = drs?.jobDetails?.project?.percentageOverhead || 0;
    return (subtotal * overheadPercentage) / 100;
  };

  const getPlanTotalCostsWithOverhead = (): number => {
    return getPlanSubtotal() + getPlanOverheadAmount();
  };

  const getPlanShiftMargin = (): number => {
    const revenue = planProjectRevenue ? parseFloat(planProjectRevenue) : 0;
    return revenue - getPlanTotalCostsWithOverhead();
  };

  // ============================================================
  // PLAN-side line-item getters (breakdown rows for the Plan panel).
  // Mirror the actual getters but read from .plan. Inclusion mirrors the
  // plan aggregate helpers so the rows sum to the displayed subtotals.
  // ============================================================

  const getPlanMobileAssets = () => {
    if (!drs) return [];
    const collect = (assets: any[]) =>
      (assets || []).filter((a) => {
        const rate = a.plan?.mobileAsset?.internalHireRate ?? a.internalHireRate;
        return a.plan?.hours != null && rate != null;
      });
    return [
      ...collect(drs.mobileAssets?.boral?.asphalt?.assets),
      ...collect(drs.mobileAssets?.boral?.spraySeal?.assets),
    ];
  };

  const getPlanAssetDescription = (asset: any) =>
    asset.plan?.mobileAsset?.id || "Unknown Asset";
  const getPlanAssetRate = (asset: any) => {
    const rate = asset.plan?.mobileAsset?.internalHireRate ?? asset.internalHireRate;
    return rate != null ? `$${rate}` : "";
  };
  const getPlanAssetQuantity = (asset: any) =>
    asset.plan?.hours != null ? asset.plan.hours : "";
  const getPlanAssetTotal = (asset: any) => {
    const rate = asset.plan?.mobileAsset?.internalHireRate ?? asset.internalHireRate;
    const hours = asset.plan?.hours;
    return rate != null && hours != null ? Number(rate) * Number(hours) : 0;
  };

  const getPlanSubcontractorData = () => {
    if (!drs?.subcontractor) return [];
    const items: any[] = [];
    const asphalt = drs.subcontractor.asphalt;
    if (asphalt) {
      (asphalt.profilingAssets || []).forEach((a: any) => {
        if (a.plan?.supplierAgreement) items.push({ type: "Subcontractor Profiling", item: a, category: "profiling" });
      });
      (asphalt.generalAssets || []).forEach((a: any) => {
        if (a.plan?.supplierAgreement) items.push({ type: "Subcontractor General", item: a, category: "general" });
      });
      (asphalt.deliveryTransport || []).forEach((t: any) => {
        if (t.plan?.supplier || t.plan?.supplierAgreement) items.push({ type: "Subcontractor Transport Delivery", item: t, category: "delivery" });
      });
      (asphalt.profilingTransport || []).forEach((t: any) => {
        if (t.plan?.supplier || t.plan?.supplierAgreement) items.push({ type: "Subcontractor Transport Profiling", item: t, category: "profilingTransport" });
      });
    }
    const sprayseal = drs.subcontractor.spraySeal;
    if (sprayseal) {
      (sprayseal.generalAssets || []).forEach((a: any) => {
        if (a.plan?.supplierAgreement) items.push({ type: "Subcontractor Spray Seal General", item: a, category: "general" });
      });
      (sprayseal.deliveryTransport || []).forEach((t: any) => {
        if (t.plan?.supplier || t.plan?.supplierAgreement) items.push({ type: "Subcontractor Spray Seal Transport Delivery", item: t, category: "delivery" });
      });
    }
    return items;
  };

  const getPlanSubcontractorRateValue = (si: any) => {
    const { item, category } = si;
    if (category === "delivery" || category === "profilingTransport") {
      return item.plan?.supplier?.unitRate ?? item.plan?.supplierAgreement?.unitRate;
    }
    return item.plan?.supplierAgreement?.unitRate;
  };
  const getPlanSubcontractorDescription = (si: any) => {
    const { item, category } = si;
    if (category === "delivery" || category === "profilingTransport") {
      return item.plan?.supplier?.name || "Unknown Transport Item";
    }
    const ag = item.plan?.supplierAgreement;
    if (ag) {
      const c = ag.cName || "";
      const d = ag.itemDescription || "";
      if (c && d) return `${c} - ${d}`;
      if (c) return c;
      if (d) return d;
      if (ag.description) return ag.description;
    }
    return "Unknown Item";
  };
  const getPlanSubcontractorRate = (si: any) => {
    const r = getPlanSubcontractorRateValue(si);
    return r != null ? `$${r}` : "";
  };
  const getPlanSubcontractorQuantity = (si: any) => {
    const { item, category } = si;
    if (category === "profiling") return item.plan?.hours ?? "";
    return item.plan?.quantity ?? "";
  };
  const getPlanSubcontractorTotal = (si: any) => {
    const { item, category } = si;
    const r = getPlanSubcontractorRateValue(si);
    const qty = category === "profiling" ? item.plan?.hours : item.plan?.quantity;
    return r != null && qty != null ? Number(r) * Number(qty) : 0;
  };

  const getPlanMaterialsData = () => {
    if (!drs?.material) return [];
    const items: any[] = [];
    (drs.material.asphalt?.detail || []).forEach((m: any, idx: number) => {
      if (m.plan?.product?.id && m.plan?.tonnes != null) {
        items.push({ type: "Asphalt Material", material: m, category: "asphalt", materialIndex: idx });
      }
    });
    (drs.material.spraySeal?.detail || []).forEach((m: any, idx: number) => {
      if (m.plan?.binderType?.id && m.plan?.residualBinder != null) {
        items.push({ type: "Spray Seal Binder", material: m, category: "spraySealBinder", materialIndex: idx });
      }
      if (m.plan?.aggregateSize?.id && m.plan?.aggregateUsed != null) {
        items.push({ type: "Spray Seal Material", material: m, category: "spraySeal", materialIndex: idx });
      }
    });
    return items;
  };

  const getPlanMaterialDescription = (mi: any) => {
    const { material, category } = mi;
    if (category === "spraySealBinder") {
      const b = material.plan?.binderType;
      return b?.description || b?.id || "Spray Binder Type";
    }
    if (category === "spraySeal") {
      const a = material.plan?.aggregateSize;
      return a?.description || a?.id || "Unknown Aggregate";
    }
    const p = material.plan?.product;
    return p?.description || p?.id || "Unknown Material";
  };
  const getPlanMaterialRate = (mi: any) => {
    const { material, category } = mi;
    const storedRate = getStoredMaterialRate(material?.id, category, "plan");

    if (category === "spraySealBinder") {
      if (storedRate !== null) return `$${storedRate}`;
      const pr = material.plan?.binderType?.price;
      return pr != null ? `$${pr}` : "$0.00";
    }
    if (category === "spraySeal") {
      // Aggregate rate is entered by hand and already stored on the DRS row, so
      // it never drifts. Read it directly to keep the rate modal responsive.
      const mr = material.materialRate;
      return mr != null ? mr.toString() : "";
    }
    if (storedRate !== null) return `$${storedRate}`;
    const pr = material.plan?.product?.price;
    return pr != null ? `$${pr}` : "";
  };
  const getPlanMaterialQuantity = (mi: any) => {
    const { material, category } = mi;
    if (category === "spraySealBinder") return material.plan?.residualBinder ?? "";
    if (category === "spraySeal") return material.plan?.aggregateUsed ?? "";
    return material.plan?.tonnes ?? "";
  };
  const getPlanMaterialTotal = (mi: any) => {
    const { material, category } = mi;
    const storedRate = getStoredMaterialRate(material?.id, category, "plan");

    if (category === "spraySealBinder") {
      const r = storedRate !== null ? storedRate : parseFloat(material.plan?.binderType?.price || "0");
      const q = parseFloat(material.plan?.residualBinder ?? "0");
      const t = r * q;
      return isFinite(t) ? t : 0;
    }
    if (category === "spraySeal") {
      const r = material.materialRate != null ? parseFloat(material.materialRate.toString()) : 0;
      const q = parseFloat(material.plan?.aggregateUsed ?? "0");
      const t = r * q;
      return isFinite(t) ? t : 0;
    }
    const r = storedRate !== null ? storedRate : material.plan?.product?.price;
    const q = material.plan?.tonnes;
    return r != null && q != null ? Number(r) * Number(q) : 0;
  };

  // Cartage and Labour only expose aggregate plan figures, so the Plan panel
  // shows a single summary line (no per-row breakdown is available).
  const getPlanCartageBase = (): number =>
    getPlanCartageSubtotal() - sumPlanCustomItems(DRSCostCategory.Cartage);
  const getPlanLabourBase = (): number => {
    const planTotal = (drs?.labour as any)?.costSummary?.planTotalCost;
    return planTotal != null ? Number(planTotal) : 0;
  };

  const planMobileAssets = getPlanMobileAssets();
  const planSubcontractorItems = getPlanSubcontractorData();
  const planMaterialItems = getPlanMaterialsData();

  const mobileAssets = getMobileAssets();

  // Get subcontractor data from the API response
  const getSubcontractorData = () => {
    if (!drs?.subcontractor) return [];

    const subcontractorItems = [];

    // Process Asphalt subcontractor data
    if (drs.subcontractor.asphalt) {
      const asphalt = drs.subcontractor.asphalt;

      // Profiling Assets - multiply unitRate * hours
      if (asphalt.profilingAssets) {
        asphalt.profilingAssets.forEach((asset) => {
          // Check if there's a valid supplier agreement with meaningful data (edit or actual only, plan data ignored)
          const agreement = asset.edit?.supplierAgreement || asset.actual?.supplierAgreement;
          const hasValidData = agreement && (agreement.cName || agreement.itemDescription || agreement.description);
          if (hasValidData) {
            subcontractorItems.push({
              type: "Subcontractor Profiling",
              item: asset,
              category: "profiling",
            });
          }
        });
      }

      // General Assets - multiply unitRate * quantity
      if (asphalt.generalAssets) {
        asphalt.generalAssets.forEach((asset) => {
          // Check if there's a valid supplier agreement with meaningful data (edit or actual only, plan data ignored)
          const agreement = asset.edit?.supplierAgreement || asset.actual?.supplierAgreement;
          const hasValidData = agreement && (agreement.cName || agreement.itemDescription || agreement.description);
          if (hasValidData) {
            subcontractorItems.push({
              type: "Subcontractor General",
              item: asset,
              category: "general",
            });
          }
        });
      }

      // Delivery Transport - no multiplication, just show if data exists (edit or actual only, plan data ignored)
      if (asphalt.deliveryTransport) {
        asphalt.deliveryTransport.forEach((transport) => {
          // Check if there's a valid supplier with meaningful data
          const supplier = transport.edit?.supplier || transport.actual?.supplier;
          const hasValidData = supplier && supplier.name;
          if (hasValidData) {
            subcontractorItems.push({
              type: "Subcontractor Transport Delivery",
              item: transport,
              category: "delivery",
            });
          }
        });
      }

      // Profiling Transport - no multiplication, just show if data exists (edit or actual only, plan data ignored)
      if (asphalt.profilingTransport) {
        asphalt.profilingTransport.forEach((transport) => {
          // Check if there's a valid supplier with meaningful data
          const supplier = transport.edit?.supplier || transport.actual?.supplier;
          const hasValidData = supplier && supplier.name;
          if (hasValidData) {
            subcontractorItems.push({
              type: "Subcontractor Transport Profiling",
              item: transport,
              category: "profilingTransport",
            });
          }
        });
      }
    }

    // Process Spray Seal subcontractor data
    if (drs.subcontractor.spraySeal) {
      const spraySeal = drs.subcontractor.spraySeal;

      // General Assets - multiply unitRate * quantity (edit or actual only, plan data ignored)
      if (spraySeal.generalAssets) {
        spraySeal.generalAssets.forEach((asset) => {
          // Check if there's a valid supplier agreement with meaningful data
          const agreement = asset.edit?.supplierAgreement || asset.actual?.supplierAgreement;
          const hasValidData = agreement && (agreement.cName || agreement.itemDescription || agreement.description);
          if (hasValidData) {
            subcontractorItems.push({
              type: "Subcontractor Spray Seal General",
              item: asset,
              category: "general",
            });
          }
        });
      }

      // Delivery Transport - no multiplication, just show if data exists (edit or actual only, plan data ignored)
      if (spraySeal.deliveryTransport) {
        spraySeal.deliveryTransport.forEach((transport) => {
          // Check if there's a valid supplier with meaningful data
          const supplier = transport.edit?.supplier || transport.actual?.supplier;
          const hasValidData = supplier && supplier.name;
          if (hasValidData) {
            subcontractorItems.push({
              type: "Subcontractor Spray Seal Transport Delivery",
              item: transport,
              category: "delivery",
            });
          }
        });
      }
    }

    return subcontractorItems;
  };

  const getSubcontractorDescription = (subcontractorItem: any) => {
    const { item, category } = subcontractorItem;

    // For transport items (delivery, profilingTransport), use supplier.name (edit or actual only, plan data ignored)
    if (category === "delivery" || category === "profilingTransport") {
      const supplier = item.edit?.supplier || item.actual?.supplier;
      return supplier?.name || "Unknown Transport Item";
    }

    // For profiling and general assets, use supplierAgreement fields (edit or actual only, plan data ignored)
    // Priority: edit -> actual
    const agreement = item.edit?.supplierAgreement || item.actual?.supplierAgreement;

    if (agreement) {
      // Concatenate cName with itemDescription when both are available
      const cName = agreement.cName || "";
      const itemDescription = agreement.itemDescription || "";

      if (cName && itemDescription) {
        return `${cName} - ${itemDescription}`;
      } else if (cName) {
        return cName;
      } else if (itemDescription) {
        return itemDescription;
      } else if (agreement.description) {
        return agreement.description;
      }
    }

    return "Unknown Item";
  };

  const getSubcontractorRate = (subcontractorItem: any) => {
    const { item, category } = subcontractorItem;

    let unitRate = null;

    if (category === "delivery" || category === "profilingTransport") {
      // For transport items, check supplier object first, then supplierAgreement
      const supplier = item.edit?.supplier || item.actual?.supplier;
      const agreement = item.edit?.supplierAgreement || item.actual?.supplierAgreement;
      unitRate = supplier?.unitRate ?? agreement?.unitRate;
    } else {
      // For profiling and general items, use supplierAgreement
      const agreement = item.edit?.supplierAgreement || item.actual?.supplierAgreement;
      unitRate = agreement?.unitRate;
    }

    return unitRate !== null && unitRate !== undefined ? `$${unitRate}` : "";
  };

  const getSubcontractorQuantity = (subcontractorItem: any) => {
    const { item, category } = subcontractorItem;

    // Priority: edit -> actual (plan data ignored)
    if (category === "profiling") {
      // For profiling assets, use hours
      const hours = item.edit?.hours ?? item.actual?.hours;
      return hours !== null && hours !== undefined ? hours : "";
    } else if (category === "general") {
      // For general assets, use quantity
      const quantity = item.edit?.quantity ?? item.actual?.quantity;
      return quantity !== null && quantity !== undefined ? quantity : "";
    } else {
      // For transport, use quantity if available
      const quantity = item.edit?.quantity ?? item.actual?.quantity;
      return quantity !== null && quantity !== undefined ? quantity : "";
    }
  };

  const getSubcontractorTotal = (subcontractorItem: any) => {
    const { item, category } = subcontractorItem;

    let unitRate = null;

    if (category === "delivery" || category === "profilingTransport") {
      // For transport items, check supplier object first, then supplierAgreement
      const supplier = item.edit?.supplier || item.actual?.supplier;
      const agreement = item.edit?.supplierAgreement || item.actual?.supplierAgreement;
      unitRate = supplier?.unitRate ?? agreement?.unitRate;
    } else {
      // For profiling and general items, use supplierAgreement
      const agreement = item.edit?.supplierAgreement || item.actual?.supplierAgreement;
      unitRate = agreement?.unitRate;
    }

    if (category === "profiling") {
      // Profiling: unitRate * hours
      const hours = item.edit?.hours ?? item.actual?.hours;
      if (unitRate !== null && unitRate !== undefined && hours !== null && hours !== undefined) {
        return unitRate * hours;
      }
    } else if (category === "general") {
      // General: unitRate * quantity
      const quantity = item.edit?.quantity ?? item.actual?.quantity;
      if (unitRate !== null && unitRate !== undefined && quantity !== null && quantity !== undefined) {
        return unitRate * quantity;
      }
    } else if (category === "delivery" || category === "profilingTransport") {
      // Transport: unitRate * quantity (if both are available)
      const quantity = item.edit?.quantity ?? item.actual?.quantity;
      if (unitRate !== null && unitRate !== undefined && quantity !== null && quantity !== undefined) {
        return unitRate * quantity;
      }
    }
    // Return 0 if no valid calculation can be made
    return 0;
  };

  const subcontractorItems = getSubcontractorData();

  // Get materials data from the API response
  const getMaterialsData = () => {
    if (!drs?.material) return [];

    const materialItems = [];

    // Process Asphalt materials (they have product with price field)
    if (drs.material.asphalt?.detail) {
      drs.material.asphalt.detail.forEach((material, materialIndex) => {
        // Priority: edit -> actual (ignore plan)
        // Check if edit has meaningful data
        const hasEditData = material.edit?.available && material.edit?.product?.id;

        // Check if actual has meaningful data
        const hasActualData = material.actual?.available && material.actual?.product?.id;

        // Only include materials that have edit or actual data (ignore plan-only materials)
        if (hasEditData || hasActualData) {
          materialItems.push({
            type: "Asphalt Material",
            material: material,
            category: "asphalt",
            materialIndex: materialIndex,
          });
        }
      });
    }

    // Process Spray Seal materials (they have aggregateSize and aggregateUsed)
    if (drs.material.spraySeal?.detail) {
      drs.material.spraySeal.detail.forEach((material, materialIndex) => {
        // Priority: edit -> actual (ignore plan)
        // Check if edit has meaningful data
        const hasEditData = material.edit?.available && (material.edit?.aggregateSize?.id || material.edit?.binderType?.id);
        
        // Check if actual has meaningful data
        const hasActualData = material.actual?.available && (material.actual?.aggregateSize?.id || material.actual?.binderType?.id);
        
        // Only include materials that have edit or actual data (ignore plan-only materials)
        if (hasEditData || hasActualData) {
          // Add binder type row if binder exists and has residualBinder
          const binderType = material.edit?.binderType || material.actual?.binderType;
          const residualBinder = material.edit?.residualBinder ?? material.actual?.residualBinder;

          if (binderType && residualBinder !== null && residualBinder !== undefined) {
            materialItems.push({
              type: 'Spray Seal Binder',
              material: material,
              category: 'spraySealBinder',
              materialIndex: materialIndex
            });
          }

          // Add aggregate row if aggregate exists and has aggregateUsed (keep existing functionality)
          const aggregateSize = material.edit?.aggregateSize || material.actual?.aggregateSize;
          const aggregateUsed = material.edit?.aggregateUsed ?? material.actual?.aggregateUsed;

          if (aggregateSize && aggregateUsed !== null && aggregateUsed !== undefined) {
            materialItems.push({
              type: 'Spray Seal Material',
              material: material,
              category: 'spraySeal',
              materialIndex: materialIndex
            });
          }
        }
      });
    }

    return materialItems;
  };

  const getMaterialDescription = (materialItem: any) => {
    const { material, category } = materialItem;
    
    if (category === 'spraySealBinder') {
      // For spray seal binder, show binder type description
      // Check if edit has meaningful binder data (id is not null), otherwise use actual
      const editBinderType = material.edit?.binderType?.id ? material.edit.binderType : null;
      const actualBinderType = material.actual?.binderType?.id ? material.actual.binderType : null;
      const binderType = editBinderType || actualBinderType;
      return binderType?.description || binderType?.id || 'Spray Binder Type';
    }
    
    if (category === 'spraySeal') {
      // For spray seal aggregate, show aggregate size description.
      // Priority edit -> actual -> plan so the row reads correctly even when
      // it only exists on the plan side (e.g. opening the rate modal from Plan).
      const editAggregateSize = material.edit?.aggregateSize?.id ? material.edit.aggregateSize : null;
      const actualAggregateSize = material.actual?.aggregateSize?.id ? material.actual.aggregateSize : null;
      const planAggregateSize = material.plan?.aggregateSize?.id ? material.plan.aggregateSize : null;
      const aggregateSize = editAggregateSize || actualAggregateSize || planAggregateSize;

      if (aggregateSize) {
        return aggregateSize.description || aggregateSize.id || "Unknown Aggregate";
      }
      return "Unknown Aggregate";
    }

    // For asphalt materials
    // Priority: edit -> actual (ignore plan)
    // Check if edit has meaningful product data (id is not null)
    const editProduct = material.edit?.product;
    const actualProduct = material.actual?.product;
    
    const product = (editProduct?.id ? editProduct : actualProduct);

    if (product?.description) {
      return product.description;
    }
    
    if (product?.id) {
      return product.id;
    }

    return "Unknown Material";
  };

  const getMaterialRate = (materialItem: any) => {
    const { material, category } = materialItem;
    
    const storedRate = getStoredMaterialRate(material?.id, category, 'actual');

    if (category === 'spraySealBinder') {
      // Prefer the rate this shift was costed at; fall back to the live binder price
      if (storedRate !== null) return `$${storedRate}`;

      // Check if edit has meaningful binder data (id is not null), otherwise use actual
      const editBinderType = material.edit?.binderType?.id ? material.edit.binderType : null;
      const actualBinderType = material.actual?.binderType?.id ? material.actual.binderType : null;
      const binderType = editBinderType || actualBinderType;

      const price = binderType?.price;
      return price !== null && price !== undefined ? `$${price}` : '$0.00';
    }

    if (category === 'spraySeal') {
      // For spray seal aggregate, use materialRate from API (top-level field, not in plan/actual/edit).
      // Entered by hand and already stored on the DRS row, so it never drifts.
      const materialRate = material.materialRate;
      return materialRate !== null && materialRate !== undefined ? materialRate.toString() : "";
    }

    // For asphalt materials
    // Prefer the rate this shift was costed at; fall back to the live product price
    if (storedRate !== null) return `$${storedRate}`;

    // Priority: edit -> actual (ignore plan)
    // Check if edit has meaningful product data (price is not null)
    const editPrice = material.edit?.product?.price;
    const actualPrice = material.actual?.product?.price;

    const price = (editPrice !== null && editPrice !== undefined) ? editPrice : actualPrice;

    return price !== null && price !== undefined ? `$${price}` : "";
  };

  const getMaterialQuantity = (materialItem: any) => {
    const { material, category } = materialItem;
    
    if (category === 'spraySealBinder') {
      // For spray seal binder, use residualBinder
      const residualBinder = material.edit?.residualBinder ?? material.actual?.residualBinder;
      return residualBinder !== null && residualBinder !== undefined ? residualBinder : '';
    }

    if (category === 'spraySeal') {
      // For spray seal aggregate, use aggregateUsed (edit -> actual -> plan).
      const aggregateUsed = material.edit?.aggregateUsed ?? material.actual?.aggregateUsed ?? material.plan?.aggregateUsed;
      return aggregateUsed !== null && aggregateUsed !== undefined ? aggregateUsed : "";
    }

    // For asphalt materials
    // Priority: edit -> actual (ignore plan)
    const editTonnes = material.edit?.tonnes;
    const actualTonnes = material.actual?.tonnes;
    
    const tonnes = (editTonnes !== null && editTonnes !== undefined) ? editTonnes : actualTonnes;

    return tonnes !== null && tonnes !== undefined ? tonnes : "";
  };

  const getMaterialTotal = (materialItem: any) => {
    const { material, category } = materialItem;
    
    const storedRate = getStoredMaterialRate(material?.id, category, 'actual');

    if (category === 'spraySealBinder') {
      // For spray seal binder, calculate from the costed rate * residualBinder
      // Check if edit has meaningful binder data (id is not null), otherwise use actual
      const editBinderType = material.edit?.binderType?.id ? material.edit.binderType : null;
      const actualBinderType = material.actual?.binderType?.id ? material.actual.binderType : null;
      const binderType = editBinderType || actualBinderType;
      const rate = storedRate !== null ? storedRate : parseFloat(binderType?.price || '0');
      const residualBinder = parseFloat(material.edit?.residualBinder ?? material.actual?.residualBinder ?? '0');
      
      // Safety check for valid numbers
      if (isNaN(rate) || isNaN(residualBinder) || !isFinite(rate) || !isFinite(residualBinder)) {
        return 0;
      }
      
      const total = rate * residualBinder;;
      
      // Safety check for the result
      if (isNaN(total) || !isFinite(total)) {
        return 0;
      }
      
      return total;
    }
    
    if (category === 'spraySeal') {
      // For spray seal aggregate, calculate from materialRate (API field) * aggregateUsed
      const materialRate = material.materialRate;
      const rate = materialRate !== null && materialRate !== undefined ? parseFloat(materialRate.toString()) : 0;

      const aggregateUsed = parseFloat(material.edit?.aggregateUsed ?? material.actual?.aggregateUsed ?? "0");

      // Safety check for valid numbers
      if (isNaN(rate) || isNaN(aggregateUsed) || !isFinite(rate) || !isFinite(aggregateUsed)) {
        return 0;
      }

      const total = rate * aggregateUsed;

      // Safety check for the result
      if (isNaN(total) || !isFinite(total)) {
        return 0;
      }

      return total;
    }

    // For asphalt materials
    // Priority: edit -> actual (ignore plan)
    // Check if edit has meaningful product data (price is not null)
    const editPrice = material.edit?.product?.price;
    const actualPrice = material.actual?.product?.price;
    const livePrice = (editPrice !== null && editPrice !== undefined) ? editPrice : actualPrice;
    const price = storedRate !== null ? storedRate : livePrice;

    // Check if edit has meaningful tonnes data
    const editTonnes = material.edit?.tonnes;
    const actualTonnes = material.actual?.tonnes;
    const tonnes = (editTonnes !== null && editTonnes !== undefined) ? editTonnes : actualTonnes;

    // If price is 0 or null, return 0
    if (price === 0 || price === null || price === undefined) {
      return 0;
    }

    if (tonnes !== null && tonnes !== undefined) {
      return price * tonnes;
    }

    return 0;
  };

  const materialItems = getMaterialsData();

  // Get cartage cost data from the API response.
  // The API sends one CARTAGE line per asphalt material. Cartage is charged on
  // the whole shift's tonnage off a plant, not per mix, so the lines for a plant
  // are merged into a single row with the tonnes and dollars added up. Without
  // this, a shift with several mixes off the same plant renders one row (they
  // all share the "Cartage - <plant>" description and collapse when plan and
  // actual rows are paired) showing only the last mix's tonnes.
  // Fuel surcharge already arrives as one aggregate line, so it passes through.
  const getCartageCostData = () => {
    if (!drs?.material) return [];

    // Type assertion to access cartageCostSummary (exists in API but not in type definition yet)
    const material = drs.material as any;
    if (!material.cartageCostSummary?.details) return [];

    const round2 = (n: number) => Number(n.toFixed(2));
    const rows: any[] = [];
    const byPlant = new Map<string, any>();

    for (const detail of material.cartageCostSummary.details) {
      if (detail?.lineType === "FUEL_SURCHARGE") {
        rows.push(detail);
        continue;
      }

      const plantKey = String(detail?.plantId ?? "");
      const existing = byPlant.get(plantKey);
      if (existing) {
        existing.tonnes = round2(Number(existing.tonnes || 0) + Number(detail?.tonnes || 0));
        existing.totalCost = round2(Number(existing.totalCost || 0) + Number(detail?.totalCost || 0));
      } else {
        const row = {
          ...detail,
          tonnes: round2(Number(detail?.tonnes || 0)),
          totalCost: round2(Number(detail?.totalCost || 0)),
        };
        byPlant.set(plantKey, row);
        rows.push(row);
      }
    }

    return rows;
  };

  const getCartageCostDescription = (cartageItem: any) => {
    if (cartageItem?.lineType === "FUEL_SURCHARGE") {
      return cartageItem.stateId ? `Fuel Surcharge - ${cartageItem.stateId}` : "Fuel Surcharge";
    }
    return cartageItem.plantId ? `Cartage - ${cartageItem.plantId}` : "Cartage Cost";
  };

  const getCartageCostRate = (cartageItem: any) => {
    // Show base cost as the rate
    const baseCost = cartageItem.baseCost;
    return baseCost !== null && baseCost !== undefined ? `$${baseCost.toFixed(2)}` : "";
  };

  const getCartageCostQuantity = (cartageItem: any) => {
    // Show tonnes as quantity
    const tonnes = cartageItem.tonnes;
    return tonnes !== null && tonnes !== undefined ? tonnes : "";
  };

  const getCartageCostTotal = (cartageItem: any) => {
    // Use the totalCost from the API
    const totalCost = cartageItem.totalCost;
    return totalCost !== null && totalCost !== undefined ? totalCost : 0;
  };

  const cartageCostItems = getCartageCostData();

  // Cost Item Management Functions
  const addNewCostItemRow = (
    category: DRSCostCategory = DRSCostCategory.ShiftCost,
    mode: "plan" | "actual" = "actual"
  ) => {
    setModalCategory(category);
    setModalMode(mode);
    setShowAddItemModal(true);
    setModalDescription("");
    setModalPrice("");
    setModalPlanPrice("");
  };

  const saveModalItem = async () => {
    if (!drs || !modalDescription.trim()) return;

    const isPlan = modalMode === "plan";

    // Only the side the dialog was opened for is saved; the other side
    // defaults to 0 (actual) / null (plan) so it stays empty.
    let priceNum = 0;
    let planPriceNum: number | null = null;

    if (isPlan) {
      if (modalPlanPrice === "") return;
      const parsedPlan = parseFloat(modalPlanPrice);
      if (isNaN(parsedPlan) || parsedPlan < 0) {
        alert("Plan price must be zero or greater.");
        return;
      }
      planPriceNum = parsedPlan;
    } else {
      if (modalPrice === "") return;
      priceNum = parseFloat(modalPrice);
      if (isNaN(priceNum) || priceNum < 0) {
        alert("Cost must be zero or greater.");
        return;
      }
    }

    setIsSavingModal(true);
    try {
      const response = await apiDRSCostItemCreate({
        drsId: drs.jobDetails.id.toString(),
        roleId: defaultRoleId,
        itemDescription: modalDescription.trim(),
        itemPrice: priceNum,
        planItemPrice: planPriceNum,
        category: modalCategory,
      });

      // Create a new cost item object using the real data from API response
      const newCostItem: DRSProjectCostItem = {
        id: response.data.costItem.id, // Use the real ID from API response
        drsSummaryId: response.data.costItem.drsSummaryId,
        itemDescription: response.data.costItem.itemDescription,
        itemPrice: parseFloat(response.data.costItem.itemPrice),
        planItemPrice: response.data.costItem.planItemPrice != null
          ? parseFloat(response.data.costItem.planItemPrice)
          : null,
        category: response.data.costItem.category ?? modalCategory,
        isDeleted: response.data.costItem.isDeleted,
        dateCreated: response.data.costItem.dateCreated,
        dateModified: response.data.costItem.dateModified,
      };

      // Add the new cost item to saved items
      setCostItems((prev) => [...prev, newCostItem]);

      // Close modal and reset fields
      setShowAddItemModal(false);
      setModalDescription("");
      setModalPrice("");
      setModalPlanPrice("");

    } catch (error) {
      console.error("Error adding cost item:", error);
    } finally {
      setIsSavingModal(false);
    }
  };

  const closeModal = () => {
    setShowAddItemModal(false);
    setModalDescription("");
    setModalPrice("");
    setModalPlanPrice("");
  };

  const handleDeleteClick = (costItem: DRSProjectCostItem) => {
    setItemToDelete(costItem);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    await deleteCostItem(itemToDelete);
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  const deleteCostItem = async (costItem: DRSProjectCostItem) => {
    if (!drs) return;

    try {
      await apiDRSCostItemDelete({
        drsId: drs.jobDetails.id.toString(),
        costItemId: costItem.id.toString(),
        roleId: defaultRoleId,
      });

      // Remove the cost item from local state
      setCostItems((prev) => prev.filter((item) => item.id !== costItem.id));

      // Update profitability after deleting cost item (if revenue is available)
      if (projectRevenue) {
        await apiDRSProfitabilityUpdate({
          drsId: drs.jobDetails.id.toString(),
          roleId: defaultRoleId,
          projectRevenue: parseFloat(projectRevenue),
        });
      }

    } catch (error) {
      console.error("Error deleting cost item:", error);
    }
  };

  const saveRevenue = async () => {
    if (!drs || projectRevenue === null || projectRevenue === undefined || projectRevenue === "") return;

    setIsSavingRevenue(true);
    try {
      await apiDRSProfitabilityUpdate({
        drsId: drs.jobDetails.id.toString(),
        roleId: defaultRoleId,
        projectRevenue: parseFloat(projectRevenue),
        comments: comments, // Always include current comments to prevent deletion
        planProjectRevenue: planProjectRevenue ? parseFloat(planProjectRevenue) : undefined,
      });

      // Refresh the DRS data to get updated values
      await refresh();

    } catch (error) {
      console.error("Error saving revenue:", error);
    } finally {
      setIsSavingRevenue(false);
    }
  };

  const savePlanRevenue = async () => {
    if (!drs || planProjectRevenue === null || planProjectRevenue === undefined || planProjectRevenue === "") return;

    setIsSavingPlanRevenue(true);
    try {
      await apiDRSProfitabilityUpdate({
        drsId: drs.jobDetails.id.toString(),
        roleId: defaultRoleId,
        planProjectRevenue: parseFloat(planProjectRevenue),
        comments: comments,
        projectRevenue: projectRevenue ? parseFloat(projectRevenue) : undefined,
      });

      await refresh();

    } catch (error) {
      console.error("Error saving plan revenue:", error);
    } finally {
      setIsSavingPlanRevenue(false);
    }
  };

  const saveComments = async () => {
    if (!drs || !comments.trim()) return;

    setIsSavingComments(true);
    try {
      await apiDRSProfitabilityUpdate({
        drsId: drs.jobDetails.id.toString(),
        roleId: defaultRoleId,
        comments: comments,
        projectRevenue: projectRevenue ? parseFloat(projectRevenue) : undefined, // Always include current revenue to prevent deletion
        planProjectRevenue: planProjectRevenue ? parseFloat(planProjectRevenue) : undefined,
      });

      // Refresh the DRS data to get updated values
      await refresh();

    } catch (error) {
      console.error("Error saving comments:", error);
    } finally {
      setIsSavingComments(false);
    }
  };

  // Spray Seal Material Rate Functions
  const openSpraySealRateModal = (materialItem: any, materialIndex: number) => {
    setSelectedSpraySealMaterial({ ...materialItem, index: materialIndex });

    // Get existing rate from API field (materialRate is at top level, not in plan/actual/edit)
    const existingRate = materialItem.material.materialRate?.toString() || "";

    setSpraySealModalCost(existingRate);
    setShowSpraySealRateModal(true);
  };

  const closeSpraySealRateModal = () => {
    setShowSpraySealRateModal(false);
    setSelectedSpraySealMaterial(null);
    setSpraySealModalCost("");
  };

  const saveSpraySealRate = async () => {
    if (!selectedSpraySealMaterial || !spraySealModalCost || !drs) return;

    setIsSavingSpraySealRate(true);
    try {
      const materialRate = parseFloat(spraySealModalCost);

      // Find the material by ID instead of using index, since index might be wrong
      const materialId = selectedSpraySealMaterial.material.id;

      // Clone the entire spray seal detail array and update the specific material's materialRate
      const updatedDetail = [...drs.material.spraySeal.detail];

      // Filter out any objects that only have materialRate (cleanup any previous duplicates)
      const cleanedDetail = updatedDetail.filter((item: any) =>
        item.id && item.materialSummaryId && (item.plan || item.actual || item.edit)
      );

      // Find and update the specific material by ID
      const materialIndexInArray = cleanedDetail.findIndex((item: any) => item.id === materialId);

      if (materialIndexInArray >= 0) {
        cleanedDetail[materialIndexInArray] = {
          ...cleanedDetail[materialIndexInArray],
          materialRate: materialRate,
        };
      } else {
        throw new Error("Material not found");
      }

      // The API skips the whole spray seal write when summary is falsy and still
      // returns 200, so a null here loses the rate silently. Send an empty object
      // instead, same as MaterialSpraySealItems.getRetained() does.
      const dataToSend = {
        summary: drs.material.spraySeal.summary || {},
        detail: cleanedDetail,
      };

      // Use the same approach as MaterialSpraySealItems.tsx - replace entire spraySeal section
      await updateSectionViaPath({
        sectionName: DRSSectionName.Material,
        path: 'spraySeal',
        data: dataToSend,
      });

      // Update local state after successful API call
      refresh();

      // Close modal and reset
      closeSpraySealRateModal();
    } catch (error) {
      console.error("Error saving spray seal rate:", error);
      // Include the API message, otherwise a 400 and a dropped connection look
      // identical and every bug report arrives with no reason attached.
      const reason =
        (error as any)?.response?.data?.message || (error as any)?.message;
      alert(reason ? `Failed to save material cost: ${reason}` : "Failed to save material cost. Please try again.");
    } finally {
      setIsSavingSpraySealRate(false);
    }
  };

  const norm = (s: any) => String(s ?? "").trim().toLowerCase();

  // Actual side rows split into main (paired against plan) and custom (listed last).
  const buildActualRows = (category: DRSCostCategory): { main: CompItem[]; custom: CompItem[] } => {
    let main: CompItem[] = [];
    if (category === DRSCostCategory.MobileAsset) {
      main = mobileAssets.map((asset, idx) => {
        const t = getAssetTotal(asset);
        const desc = getAssetDescription(asset);
        return { identity: norm(desc), description: desc, rate: getAssetRate(asset), rateText: getAssetRate(asset), qty: getAssetQuantity(asset), total: t > 0 ? `$${t}` : "", totalNum: t, key: `a-asset-${asset.id || idx}` };
      });
    } else if (category === DRSCostCategory.Subcontractor) {
      main = subcontractorItems.map((subItem, idx) => {
        const t = getSubcontractorTotal(subItem);
        const desc = getSubcontractorDescription(subItem);
        return { identity: norm(desc), description: desc, rate: getSubcontractorRate(subItem), rateText: getSubcontractorRate(subItem), qty: getSubcontractorQuantity(subItem), total: t > 0 ? `$${t}` : "", totalNum: t, key: `a-sub-${subItem.type}-${idx}` };
      });
    } else if (category === DRSCostCategory.Material) {
      main = materialItems.map((materialItem, idx) => {
        const isSpraySeal = materialItem.category === "spraySeal";
        const rateValue = getMaterialRate(materialItem);
        const t = getMaterialTotal(materialItem);
        const desc = getMaterialDescription(materialItem);
        return {
          // Key by physical detail row (category + row index) so rows sharing a
          // description (e.g. three "BITUMEN C170" lines) don't collapse into one.
          identity: `${materialItem.category}-${materialItem.materialIndex}`,
          description: desc,
          rate: isSpraySeal ? (
            <EditRateButton onClick={() => openSpraySealRateModal(materialItem, idx)}>
              {rateValue ? `$${rateValue}` : "+ Add Cost"}
            </EditRateButton>
          ) : (
            getMaterialRate(materialItem)
          ),
          rateText: String(rateValue ?? ""),
          qty: getMaterialQuantity(materialItem),
          total: t > 0 ? `$${t.toFixed(2)}` : "$0.00",
          totalNum: t,
          key: `a-mat-${materialItem.category}-${idx}`,
        };
      });
    } else if (category === DRSCostCategory.Cartage) {
      main = cartageCostItems.map((cartageItem: any, idx: number) => {
        const t = getCartageCostTotal(cartageItem);
        const desc = getCartageCostDescription(cartageItem);
        return { identity: norm(desc), description: desc, rate: getCartageCostRate(cartageItem), rateText: getCartageCostRate(cartageItem), qty: getCartageCostQuantity(cartageItem), total: t > 0 ? `$${safeToFixed(t, 2)}` : "$0.00", totalNum: t, key: `a-cart-${cartageItem.materialId}-${idx}` };
      });
    } else if (category === DRSCostCategory.Labour) {
      main = [{ identity: "labour", description: isLoadingLabourCost ? "Loading labour cost..." : "Total Labour Cost", total: isLoadingLabourCost ? "" : labourCost > 0 ? `$${safeToFixed(labourCost, 2)}` : "$0.00", totalNum: labourCost, key: "a-labour" }];
    }
    const custom = getActualCustomItemsForCategory(category).map((it) => ({
      identity: norm(it.itemDescription),
      description: it.itemDescription,
      total: `$${Number(it.itemPrice || 0).toFixed(2)}`,
      totalNum: Number(it.itemPrice || 0),
      onDelete: () => handleDeleteClick(it),
      key: `a-cust-${it.id}`,
    }));
    return { main, custom };
  };

  const buildPlanRows = (category: DRSCostCategory): { main: CompItem[]; custom: CompItem[] } => {
    let main: CompItem[] = [];
    if (category === DRSCostCategory.MobileAsset) {
      main = planMobileAssets.map((asset, idx) => {
        const t = getPlanAssetTotal(asset);
        const desc = getPlanAssetDescription(asset);
        return { identity: norm(desc), description: desc, rate: getPlanAssetRate(asset), rateText: getPlanAssetRate(asset), qty: getPlanAssetQuantity(asset), total: t > 0 ? `$${t}` : "", totalNum: t, key: `p-asset-${asset.id || idx}` };
      });
    } else if (category === DRSCostCategory.Subcontractor) {
      main = planSubcontractorItems.map((subItem, idx) => {
        const t = getPlanSubcontractorTotal(subItem);
        const desc = getPlanSubcontractorDescription(subItem);
        return { identity: norm(desc), description: desc, rate: getPlanSubcontractorRate(subItem), rateText: getPlanSubcontractorRate(subItem), qty: getPlanSubcontractorQuantity(subItem), total: t > 0 ? `$${t}` : "", totalNum: t, key: `p-sub-${subItem.type}-${idx}` };
      });
    } else if (category === DRSCostCategory.Material) {
      main = planMaterialItems.map((materialItem, idx) => {
        const isSpraySeal = materialItem.category === "spraySeal";
        const rateValue = getPlanMaterialRate(materialItem);
        const t = getPlanMaterialTotal(materialItem);
        const desc = getPlanMaterialDescription(materialItem);
        return {
          // Match the actual-side key so plan and actual copies of the same
          // detail row pair up instead of collapsing by shared description.
          identity: `${materialItem.category}-${materialItem.materialIndex}`,
          description: desc,
          // The spray seal aggregate rate is a single shared field driving both
          // plan and actual totals, so it stays editable from the plan side too.
          rate: isSpraySeal ? (
            <EditRateButton onClick={() => openSpraySealRateModal(materialItem, idx)}>
              {rateValue ? `$${rateValue}` : "+ Add Cost"}
            </EditRateButton>
          ) : (
            rateValue
          ),
          rateText: String(rateValue ?? ""),
          qty: getPlanMaterialQuantity(materialItem),
          total: t > 0 ? `$${t.toFixed(2)}` : "$0.00",
          totalNum: t,
          key: `p-mat-${materialItem.category}-${idx}`,
        };
      });
    } else if (category === DRSCostCategory.Cartage) {
      const t = getPlanCartageBase();
      main = [{ identity: norm("Cartage Cost"), description: "Total Cartage Cost", total: t > 0 ? `$${safeToFixed(t)}` : "$0.00", totalNum: t, key: "p-cart" }];
    } else if (category === DRSCostCategory.Labour) {
      const t = getPlanLabourBase();
      main = [{ identity: "labour", description: "Total Labour Cost", total: t > 0 ? `$${safeToFixed(t)}` : "$0.00", totalNum: t, key: "p-labour" }];
    }
    const custom = getPlanCustomItemsForCategory(category).map((it) => ({
      identity: norm(it.itemDescription),
      description: it.itemDescription,
      total: `$${Number(it.planItemPrice ?? 0).toFixed(2)}`,
      totalNum: Number(it.planItemPrice ?? 0),
      onDelete: () => handleDeleteClick(it),
      key: `p-cust-${it.id}`,
    }));
    return { main, custom };
  };

  // Pair plan and actual rows by identity so matching items line up. Custom
  // items are paired separately and rendered after the main items.
  const buildPairedRows = (category: DRSCostCategory): PairedRow[] => {
    const plan = buildPlanRows(category);
    const actual = buildActualRows(category);
    const pair = (planItems: CompItem[], actualItems: CompItem[], isCustom: boolean): PairedRow[] => {
      const map = new Map<string, PairedRow>();
      const order: string[] = [];
      planItems.forEach((it) => {
        const k = it.identity;
        if (!map.has(k)) { map.set(k, { key: `${isCustom ? "c" : "m"}-${category}-${k}`, description: it.description, isCustom }); order.push(k); }
        map.get(k)!.plan = it;
      });
      actualItems.forEach((it) => {
        const k = it.identity;
        if (!map.has(k)) { map.set(k, { key: `${isCustom ? "c" : "m"}-${category}-${k}`, description: it.description, isCustom }); order.push(k); }
        map.get(k)!.actual = it;
      });
      return order.map((k) => map.get(k)!);
    };
    return [...pair(plan.main, actual.main, false), ...pair(plan.custom, actual.custom, true)];
  };

  const COST_SECTIONS = [
    { key: "assets", label: "Assets", category: DRSCostCategory.MobileAsset, planSubtotal: getPlanMobileAssetsSubtotal(), actualSubtotal: getMobileAssetsSubtotal() },
    { key: "subcontractor", label: "Subcontractor", category: DRSCostCategory.Subcontractor, planSubtotal: getPlanSubcontractorSubtotal(), actualSubtotal: getSubcontractorSubtotal() },
    { key: "materials", label: "Materials", category: DRSCostCategory.Material, planSubtotal: getPlanMaterialsSubtotal(), actualSubtotal: getMaterialsSubtotal() },
    { key: "cartage", label: "Cartage Cost", category: DRSCostCategory.Cartage, planSubtotal: getPlanCartageSubtotal(), actualSubtotal: getCartageCostSubtotal() },
    { key: "labour", label: "Labour", category: DRSCostCategory.Labour, planSubtotal: getPlanLabourSubtotal(), actualSubtotal: getLabourSubtotal() },
    { key: "custom", label: "Custom Items", category: DRSCostCategory.ShiftCost, planSubtotal: getPlanCustomItemsSubtotal(), actualSubtotal: getCustomItemsSubtotal() },
  ];

  const overheadPct = drs?.jobDetails?.project?.percentageOverhead || 0;

  return (
    <Wrapper>
      <Header>
        <Title>Shift Profitability</Title>
        <ProjectInfo>
          <InfoBox>
            <Label>Project Revenue</Label>
            <Value>(Scenario)</Value>
          </InfoBox>
          <InfoBox>
            <Label>Revenue to Date</Label>
            <Value>(AIMS)</Value>
          </InfoBox>
          <InfoBox>
            <Label>Quoted Shifts</Label>
            <Value>(Scenario)</Value>
          </InfoBox>
          <InfoBox>
            <Label>Quoted Margin</Label>
            <Value>(Scenario)</Value>
          </InfoBox>
        </ProjectInfo>
      </Header>

      {drs?.jobDetails?.id && <ActivityHistory drsSummaryId={drs.jobDetails.id} />}

      <CompContainer>
        <CompRow>
          <CompHeadLabelCell />
          <CompHeadCell>PLAN</CompHeadCell>
          <CompHeadCell>ACTUAL</CompHeadCell>
          <CompVarHeadCell>PLAN vs ACTUAL</CompVarHeadCell>
        </CompRow>

        <CompRow>
          <CompLabelCell>Revenue</CompLabelCell>
          <CompSideCell>
            <Input value={planProjectRevenue} onChange={(e) => setPlanProjectRevenue(e.target.value)} placeholder="0.00" type="number" step="0.01" style={{ flex: 1 }} />
            <SaveButton onClick={savePlanRevenue} disabled={isSavingPlanRevenue}>
              {isSavingPlanRevenue ? "Saving..." : "Save"}
            </SaveButton>
          </CompSideCell>
          <CompSideCell>
            <Input value={projectRevenue} onChange={(e) => setProjectRevenue(e.target.value)} placeholder="0.00" type="number" step="0.01" style={{ flex: 1 }} />
            <SaveButton onClick={saveRevenue} disabled={isSavingRevenue}>
              {isSavingRevenue ? "Saving..." : "Save"}
            </SaveButton>
          </CompSideCell>
          <CompVarCell>
            <Variance plan={planProjectRevenue ? parseFloat(planProjectRevenue) : 0} actual={projectRevenue ? parseFloat(projectRevenue) : 0} safeToFixed={safeToFixed} />
          </CompVarCell>
        </CompRow>

        {COST_SECTIONS.map((sec) => (
          <ComparisonCategory
            key={sec.key}
            label={sec.label}
            planSubtotal={sec.planSubtotal}
            actualSubtotal={sec.actualSubtotal}
            rows={buildPairedRows(sec.category)}
            expanded={!!expandedCats[sec.key]}
            onToggle={() => toggleCat(sec.key)}
            onAddPlan={sec.category === DRSCostCategory.ShiftCost ? () => addNewCostItemRow(DRSCostCategory.ShiftCost, "plan") : undefined}
            onAddActual={sec.category === DRSCostCategory.ShiftCost ? () => addNewCostItemRow(DRSCostCategory.ShiftCost, "actual") : undefined}
            safeToFixed={safeToFixed}
          />
        ))}

        <CompRow $bg="#e8eaed">
          <CompLabelCell>Sub total</CompLabelCell>
          <CompTotalCell>${safeToFixed(getPlanSubtotal())}</CompTotalCell>
          <CompTotalCell>${safeToFixed(getSubtotal())}</CompTotalCell>
          <CompVarCell>
            <Variance plan={getPlanSubtotal()} actual={getSubtotal()} favorableWhenLower safeToFixed={safeToFixed} />
          </CompVarCell>
        </CompRow>
        <CompRow $bg="#f0f0f0">
          <CompLabelCell>Overhead ({overheadPct}%)</CompLabelCell>
          <CompTotalCell>${safeToFixed(getPlanOverheadAmount())}</CompTotalCell>
          <CompTotalCell>${safeToFixed(getOverheadAmount())}</CompTotalCell>
          <CompVarCell>
            <Variance plan={getPlanOverheadAmount()} actual={getOverheadAmount()} favorableWhenLower safeToFixed={safeToFixed} />
          </CompVarCell>
        </CompRow>
        <CompRow $bg="#d6d8da">
          <CompLabelCell>Total Costs</CompLabelCell>
          <CompTotalCell>${safeToFixed(getPlanTotalCostsWithOverhead())}</CompTotalCell>
          <CompTotalCell>${safeToFixed(getTotalCostsWithOverhead())}</CompTotalCell>
          <CompVarCell>
            <Variance plan={getPlanTotalCostsWithOverhead()} actual={getTotalCostsWithOverhead()} favorableWhenLower safeToFixed={safeToFixed} />
          </CompVarCell>
        </CompRow>
        <CompRow $bg="#eef4ee">
          <CompLabelCell>Margin</CompLabelCell>
          <CompTotalCell>${safeToFixed(getPlanShiftMargin())}</CompTotalCell>
          <CompTotalCell>${safeToFixed(getShiftMargin())}</CompTotalCell>
          <CompVarCell>
            <Variance plan={getPlanShiftMargin()} actual={getShiftMargin()} safeToFixed={safeToFixed} />
          </CompVarCell>
        </CompRow>
      </CompContainer>

      <Footer>
        <CommentsBox>
          <CommentsHeader>
            <FooterLabel>Comments</FooterLabel>
            <SaveButton onClick={saveComments} disabled={isSavingComments}>
              {isSavingComments ? "Saving..." : "Save Comments"}
            </SaveButton>
          </CommentsHeader>
          <CommentsInput value={comments} onChange={(e) => setComments(e.target.value)} placeholder="Add comments..." />
        </CommentsBox>
      </Footer>

      {/* Add Cost Item Modal */}
      {showAddItemModal && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>{modalMode === "plan" ? "Add Plan Cost Item" : "Add Cost Item"}</ModalHeader>
            <ModalBody>
              <ModalField>
                <ModalLabel>Category</ModalLabel>
                <ModalSelect value={modalCategory} onChange={(e) => setModalCategory(Number(e.target.value) as DRSCostCategory)}>
                  {[
                    DRSCostCategory.ShiftCost,
                    DRSCostCategory.MobileAsset,
                    DRSCostCategory.Subcontractor,
                    DRSCostCategory.Material,
                    DRSCostCategory.Cartage,
                    DRSCostCategory.Labour,
                  ].map((cat) => (
                    <option key={cat} value={cat}>{COST_CATEGORY_LABELS[cat]}</option>
                  ))}
                </ModalSelect>
              </ModalField>
              <ModalField>
                <ModalLabel>Item Description</ModalLabel>
                <ModalInput type="text" value={modalDescription} onChange={(e) => setModalDescription(e.target.value)} placeholder="Enter item description..." />
              </ModalField>
              {modalMode === "plan" ? (
                <ModalField>
                  <ModalLabel>Plan Price</ModalLabel>
                  <ModalInput type="number" step="0.01" min="0" value={modalPlanPrice} onChange={(e) => setModalPlanPrice(e.target.value)} placeholder="0.00" />
                </ModalField>
              ) : (
                <ModalField>
                  <ModalLabel>Actual Price</ModalLabel>
                  <ModalInput type="number" step="0.01" min="0" value={modalPrice} onChange={(e) => setModalPrice(e.target.value)} placeholder="0.00" />
                </ModalField>
              )}
            </ModalBody>
            <ModalFooter>
              <ModalButton onClick={closeModal} variant="cancel">
                Cancel
              </ModalButton>
              <ModalButton onClick={saveModalItem} disabled={isSavingModal || !modalDescription.trim() || (modalMode === "plan" ? !modalPlanPrice : !modalPrice)} variant="save">
                {isSavingModal ? "Saving..." : "Save"}
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && itemToDelete && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <h3>Confirm Delete</h3>
            </ModalHeader>
            <ModalBody>
              <p>Are you sure you want to delete the cost item:</p>
              <strong>"{itemToDelete.itemDescription}"</strong>
              <p>This action cannot be undone.</p>
            </ModalBody>
            <ModalFooter>
              <ModalButton onClick={cancelDelete} variant="cancel">
                Cancel
              </ModalButton>
              <DeleteButton onClick={confirmDelete}>Delete</DeleteButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Spray Seal Material Rate Modal */}
      {showSpraySealRateModal && selectedSpraySealMaterial && (
        <ModalOverlay onClick={closeSpraySealRateModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>Set Material Cost</ModalHeader>
            <ModalBody>
              <ModalField>
                <ModalLabel>Material</ModalLabel>
                <ModalReadOnlyText>{getMaterialDescription(selectedSpraySealMaterial)}</ModalReadOnlyText>
              </ModalField>
              <ModalField>
                <ModalLabel>Aggregate Used</ModalLabel>
                <ModalReadOnlyText>{getMaterialQuantity(selectedSpraySealMaterial)}</ModalReadOnlyText>
              </ModalField>
              <ModalField>
                <ModalLabel>Cost per Unit ($)</ModalLabel>
                <ModalInput type="number" step="0.01" value={spraySealModalCost} onChange={(e) => setSpraySealModalCost(e.target.value)} placeholder="0.00" />
              </ModalField>
              {spraySealModalCost && getMaterialQuantity(selectedSpraySealMaterial) && (
                <ModalField>
                  <ModalLabel>Total Cost</ModalLabel>
                  <ModalReadOnlyText style={{ fontWeight: 600, color: "#28a745" }}>
                    ${(parseFloat(spraySealModalCost) * parseFloat(getMaterialQuantity(selectedSpraySealMaterial).toString())).toFixed(2)}
                  </ModalReadOnlyText>
                </ModalField>
              )}
            </ModalBody>
            <ModalFooter>
              <ModalButton onClick={closeSpraySealRateModal} variant="cancel">
                Cancel
              </ModalButton>
              <ModalButton onClick={saveSpraySealRate} disabled={isSavingSpraySealRate || !spraySealModalCost} variant="save">
                {isSavingSpraySealRate ? "Saving..." : "Save"}
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

    </Wrapper>
  );
};

// A single comparison row item (one line under a category, for either side).
type CompItem = {
  key: string;
  identity: string;
  description: React.ReactNode;
  rate?: React.ReactNode;
  rateText?: string;
  qty?: React.ReactNode;
  total: string;
  totalNum: number;
  onDelete?: () => void;
};

// A plan row and its matching actual row, lined up by identity.
type PairedRow = {
  key: string;
  description: React.ReactNode;
  isCustom: boolean;
  plan?: CompItem;
  actual?: CompItem;
};

// Classify a paired row by comparing plan vs actual values.
function pairedStatus(row: PairedRow): "unchanged" | "changed" | "new" | "removed" {
  if (row.plan && row.actual) {
    const same =
      (row.plan.rateText ?? "") === (row.actual.rateText ?? "") &&
      String(row.plan.qty ?? "") === String(row.actual.qty ?? "") &&
      row.plan.totalNum === row.actual.totalNum;
    return same ? "unchanged" : "changed";
  }
  return row.actual ? "new" : "removed";
}

const STATUS_LABEL: Record<string, string> = {
  changed: "Changed",
  new: "New",
  removed: "Not in actual",
  unchanged: "",
};

// One side's rate / qty / total cells (or a muted placeholder when absent).
function SideValues({ item }: { item?: CompItem }) {
  if (!item) {
    return (
      <SideVals>
        <span />
        <span />
        <SideMuted>—</SideMuted>
        <span />
      </SideVals>
    );
  }
  return (
    <SideVals>
      <SideNum>{item.rate ?? ""}</SideNum>
      <SideNum>{item.qty ?? ""}</SideNum>
      <SideNum>{item.total}</SideNum>
      <SideAct>{item.onDelete && <DeleteActionButton onClick={item.onDelete}>🗑</DeleteActionButton>}</SideAct>
    </SideVals>
  );
}

// One collapsible category: aligned plan/actual subtotals in the header,
// expanding to a row-by-row paired comparison of items.
function ComparisonCategory({
  label,
  planSubtotal,
  actualSubtotal,
  rows,
  expanded,
  onToggle,
  onAddPlan,
  onAddActual,
  safeToFixed,
}: {
  label: string;
  planSubtotal: number;
  actualSubtotal: number;
  rows: PairedRow[];
  expanded: boolean;
  onToggle: () => void;
  onAddPlan?: () => void;
  onAddActual?: () => void;
  safeToFixed: (value: any, decimals?: number) => string;
}) {
  return (
    <>
      <CompRow $clickable onClick={onToggle}>
        <CompLabelCell>
          <Chevron $open={expanded}>▶</Chevron>
          {label}
        </CompLabelCell>
        <CompTotalCell>${safeToFixed(planSubtotal)}</CompTotalCell>
        <CompTotalCell>${safeToFixed(actualSubtotal)}</CompTotalCell>
        <CompVarCell>
          <Variance plan={planSubtotal} actual={actualSubtotal} favorableWhenLower safeToFixed={safeToFixed} />
        </CompVarCell>
      </CompRow>
      {expanded && (
        <>
          <CompSubHeadRow>
            <CompRowDesc>Item</CompRowDesc>
            <SideVals>
              <SideNumHead>Rate</SideNumHead>
              <SideNumHead>Qty</SideNumHead>
              <SideNumHead>Total</SideNumHead>
              <span />
            </SideVals>
            <SideVals>
              <SideNumHead>Rate</SideNumHead>
              <SideNumHead>Qty</SideNumHead>
              <SideNumHead>Total</SideNumHead>
              <span />
            </SideVals>
            <CompVarCell />
          </CompSubHeadRow>
          {rows.length === 0 && !onAddPlan && !onAddActual && (
            <CompItemRow $status="unchanged">
              <CompRowDesc>
                <SideMuted>No items</SideMuted>
              </CompRowDesc>
              <SideVals />
              <SideVals />
              <CompVarCell />
            </CompItemRow>
          )}
          {rows.map((row) => {
            const status = pairedStatus(row);
            return (
              <CompItemRow key={row.key} $status={status}>
                <CompRowDesc>
                  <span>{row.description}</span>
                  {STATUS_LABEL[status] && <StatusTag $status={status}>{STATUS_LABEL[status]}</StatusTag>}
                </CompRowDesc>
                <SideValues item={row.plan} />
                <SideValues item={row.actual} />
                <CompVarCell>
                  {row.plan && row.actual ? (
                    <Variance plan={row.plan.totalNum} actual={row.actual.totalNum} favorableWhenLower safeToFixed={safeToFixed} />
                  ) : (
                    <StatusTag $status={status}>{STATUS_LABEL[status]}</StatusTag>
                  )}
                </CompVarCell>
              </CompItemRow>
            );
          })}
          {(onAddPlan || onAddActual) && (
            <CompItemRow $status="unchanged">
              <CompRowDesc />
              <SideVals $add>{onAddPlan && <AddNewItemButton onClick={onAddPlan}>+ Add Plan Cost</AddNewItemButton>}</SideVals>
              <SideVals $add>{onAddActual && <AddNewItemButton onClick={onAddActual}>+ Add Actual Cost</AddNewItemButton>}</SideVals>
              <CompVarCell />
            </CompItemRow>
          )}
        </>
      )}
    </>
  );
}

// Plan vs Actual comparison cell. For costs, favorableWhenLower=true means
// coming in under plan is good (green); for revenue/margin, higher is good.
function Variance({
  plan,
  actual,
  favorableWhenLower = false,
  safeToFixed,
}: {
  plan: number;
  actual: number;
  favorableWhenLower?: boolean;
  safeToFixed: (value: any, decimals?: number) => string;
}) {
  const p = Number(plan) || 0;
  const a = Number(actual) || 0;
  const delta = a - p;

  if (p === 0 && a === 0) return <VarPct $tone="neutral">—</VarPct>;

  const pct = p !== 0 ? (delta / p) * 100 : null;
  const tone = delta === 0 ? "neutral" : (favorableWhenLower ? delta < 0 : delta > 0) ? "good" : "bad";
  const word = favorableWhenLower
    ? delta < 0 ? "under" : delta > 0 ? "over" : "on plan"
    : delta > 0 ? "above" : delta < 0 ? "below" : "on plan";
  const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "";

  return (
    <>
      <VarPct $tone={tone}>
        {delta === 0 ? "on plan" : pct == null ? `${arrow} ${word}` : `${arrow} ${Math.abs(pct).toFixed(1)}% ${word}`}
      </VarPct>
      {delta !== 0 && (
        <VarAmt>{`${delta < 0 ? "-" : "+"}$${safeToFixed(Math.abs(delta))}`}</VarAmt>
      )}
    </>
  );
}

const Wrapper = styled.div`
  padding: 20px 0;
`;

const CompVarHeadCell = styled.div`
  flex: 0 0 150px;
  padding: 8px 14px;
  text-align: center;
  background: #2d5016;
  color: #fff;
  font-weight: 700;
  letter-spacing: 0.5px;
  font-size: 12px;
`;

const CompVarCell = styled.div`
  flex: 0 0 150px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 3px;
`;

const VarPct = styled.span<{ $tone: "good" | "bad" | "neutral" }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  background: ${(p) => (p.$tone === "good" ? "#e1f4e3" : p.$tone === "bad" ? "#fde2e2" : "#eee")};
  color: ${(p) => (p.$tone === "good" ? "#1f7a36" : p.$tone === "bad" ? "#a8071a" : "#667085")};
`;

const VarAmt = styled.span`
  font-size: 11px;
  color: #667085;
  white-space: nowrap;
`;

const CompContainer = styled.div`
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  margin-bottom: 20px;
`;

const CompRow = styled.div<{ $clickable?: boolean; $bg?: string }>`
  display: flex;
  align-items: stretch;
  border-top: 1px solid #ececec;
  background: ${(p) => p.$bg || "transparent"};
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};

  &:first-child {
    border-top: none;
  }
  &:hover {
    background: ${(p) => (p.$clickable ? "#f7f9fb" : p.$bg || "transparent")};
  }
`;

const CompHeadLabelCell = styled.div`
  flex: 0 0 160px;
  background: #2d5016;
  border-right: 1px solid #24410f;
`;

const CompHeadCell = styled.div`
  flex: 1 1 0;
  min-width: 0;
  padding: 8px 16px;
  text-align: center;
  background: #2d5016;
  color: #fff;
  font-weight: 700;
  letter-spacing: 1px;
  border-right: 1px solid #24410f;

  &:last-child {
    border-right: none;
  }
`;

const CompLabelCell = styled.div<{ $sub?: boolean }>`
  flex: 0 0 160px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #344054;
  border-right: 1px solid #ececec;
`;

const CompTotalCell = styled.div`
  flex: 1 1 0;
  min-width: 0;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-weight: 700;
  color: #1a202c;
  border-right: 1px solid #ececec;

  &:last-child {
    border-right: none;
  }
`;

const CompSideCell = styled.div`
  flex: 1 1 0;
  min-width: 0;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-right: 1px solid #ececec;

  &:last-child {
    border-right: none;
  }
`;

const Chevron = styled.span<{ $open?: boolean }>`
  display: inline-block;
  font-size: 10px;
  color: #98a2b3;
  transition: transform 0.15s ease;
  transform: rotate(${(p) => (p.$open ? "90deg" : "0deg")});
`;

const CompSubHeadRow = styled.div`
  display: flex;
  align-items: stretch;
  border-top: 1px solid #ececec;
  background: #f4f6f8;
`;

const CompItemRow = styled.div<{ $status: "unchanged" | "changed" | "new" | "removed" }>`
  display: flex;
  align-items: stretch;
  border-top: 1px solid #f0f1f3;
  background: ${(p) =>
    p.$status === "changed"
      ? "#fff7e6"
      : p.$status === "new"
      ? "#e8f1fd"
      : p.$status === "removed"
      ? "#f3f4f6"
      : "#ffffff"};
`;

const CompRowDesc = styled.div`
  flex: 0 0 160px;
  padding: 8px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  font-size: 13px;
  color: #344054;
  border-right: 1px solid #ececec;
  overflow-wrap: anywhere;
`;

const SideVals = styled.div<{ $add?: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  padding: 8px 12px;
  border-right: 1px solid #ececec;
  ${(p) =>
    p.$add
      ? ""
      : `display: grid; grid-template-columns: 1fr 1fr 1.2fr 28px; gap: 6px; align-items: center;`}

  &:last-child {
    border-right: none;
  }
`;

const SideNum = styled.div`
  text-align: right;
  color: #475467;
  font-size: 13px;
  min-width: 0;
`;

const SideNumHead = styled.div`
  text-align: right;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: #98a2b3;
  text-transform: uppercase;
`;

const SideAct = styled.div`
  display: flex;
  justify-content: center;
`;

const SideMuted = styled.span`
  color: #b0b7c3;
  font-style: italic;
  font-size: 12px;
`;

const StatusTag = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  background: ${(p) =>
    p.$status === "changed" ? "#ffe7ba" : p.$status === "new" ? "#d6e8fd" : p.$status === "removed" ? "#e4e7eb" : "#eee"};
  color: ${(p) =>
    p.$status === "changed" ? "#ad6800" : p.$status === "new" ? "#1d4ed8" : "#667085"};
`;

const Header = styled.div`
  margin-bottom: 20px;
`;

const Title = styled.h2`
  color: #4a90e2;
  margin-bottom: 15px;
  font-size: 24px;
  font-weight: 600;
`;

const ProjectInfo = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;

const InfoBox = styled.div`
  background: #e8f5e8;
  border: 1px solid #c8e6c8;
  border-radius: 4px;
  padding: 8px 12px;
  min-width: 120px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #666;
`;

const Value = styled.div`
  font-size: 14px;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  padding: 4px;
  text-align: center;

  &:focus {
    outline: 1px solid #4a90e2;
    background: white;
  }
`;

const SaveButton = styled.button`
  background: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background: #357abd;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const Footer = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
`;

const FooterLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #666;
  margin-bottom: 8px;
`;

const CommentsBox = styled.div`
  background: #e8f5e8;
  border: 1px solid #c8e6c8;
  border-radius: 4px;
  padding: 12px;
  flex: 1;
`;

const CommentsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const CommentsInput = styled.textarea`
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 14px;
  min-height: 60px;
  resize: vertical;
`;

// Action cell and button styles for cost items

const ActionButton = styled.button`
  border: none;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DeleteActionButton = styled(ActionButton)`
  background: #dc3545;
  color: white;

  &:hover {
    background: #c82333;
  }
`;

const AddNewItemButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  width: 100%;

  &:hover {
    background: #218838;
  }
`;

// Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 0;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
  font-size: 18px;
  font-weight: 600;
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const ModalField = styled.div`
  margin-bottom: 20px;
`;

const ModalLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const ModalSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ModalButton = styled.button<{ variant: "cancel" | "save" }>`
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid;

  ${(props) =>
    props.variant === "cancel"
      ? `
    background: white;
    color: #6c757d;
    border-color: #6c757d;
    
    &:hover {
      background: #f8f9fa;
    }
  `
      : `
    background: #28a745;
    color: white;
    border-color: #28a745;
    
    &:hover:not(:disabled) {
      background: #218838;
      border-color: #1e7e34;
    }
    
    &:disabled {
      background: #6c757d;
      border-color: #6c757d;
      cursor: not-allowed;
    }
  `}
`;

const DeleteButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin-left: 10px;
  background-color: #dc3545;
  color: white;
  border: 1px solid #dc3545;

  &:hover {
    background-color: #c82333;
    border-color: #bd2130;
  }
`;

const EditRateButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: background-color 0.2s;

  &:hover {
    background: #218838;
  }
`;

const ModalReadOnlyText = styled.div`
  padding: 10px 12px;
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
`;

export default observer(ShiftProfitability);
